import { useId } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import type { Key } from 'react-aria-components'
import { ButtonGroup, ButtonGroupItem } from '@/components/base/button-group/button-group'
import { Button } from '@/components/base/buttons'
import { cn } from '@/lib/utils'

export interface FormScoreSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  /** Visible field label. */
  label: string
  /** Accessible label (and tooltip) for the clear button, e.g. "No score". */
  clearLabel: string
  /** Accessible label for a single segment, e.g. (2, 5) => "2 of 5". */
  segmentLabel?: (value: number, max: number) => string
  /** Number of segments (defaults to 5). */
  max?: number
  isDisabled?: boolean
  className?: string
}

/**
 * FormScoreSelect - a react-hook-form integrated 1..max score picker.
 *
 * Renders the score as a row of clickable segments - a clickable counterpart
 * of the customer-facing ScoreBar: picking N fills segments 1..N in brand
 * teal. The underlying ToggleButtonGroup (selectionMode "single") exposes
 * role="radiogroup" with role="radio" segments and arrow-key navigation.
 *
 * Field value contract (matches the existing string-based form schemas):
 * '' when unscored, otherwise String(1..max). The clear button (and
 * re-clicking the selected segment) writes ''.
 */
export function FormScoreSelect<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  clearLabel,
  segmentLabel,
  max = 5,
  isDisabled,
  className,
}: FormScoreSelectProps<TFieldValues>) {
  const { t } = useTranslation()
  const labelId = useId()
  const {
    field,
    fieldState: { error },
  } = useController({ control, name })

  const raw = typeof field.value === 'string' ? field.value : ''
  const selected = raw.trim() === '' ? null : Number(raw)
  const segments = Array.from({ length: max }, (_, i) => i + 1)

  const handleSelectionChange = (keys: Set<Key>) => {
    const [key] = keys
    field.onChange(key == null ? '' : String(key))
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <span id={labelId} className="text-sm font-medium text-gray-700 dark:text-gray-200">
        {label}
      </span>
      <div className="flex items-center gap-1">
        <ButtonGroup
          size="sm"
          aria-labelledby={labelId}
          selectedKeys={selected != null ? [String(selected)] : []}
          onSelectionChange={handleSelectionChange}
          isDisabled={isDisabled}
          className="flex w-full max-w-xs"
        >
          {segments.map((segment) => {
            const isFilled = selected != null && segment <= selected
            return (
              <ButtonGroupItem
                key={segment}
                id={String(segment)}
                aria-label={segmentLabel ? segmentLabel(segment, max) : undefined}
                className={cn(
                  'flex-1 justify-center px-0',
                  isFilled &&
                    'bg-brand-500 selected:bg-brand-500 selected:text-white hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-400 text-white hover:text-white'
                )}
              >
                {segment}
              </ButtonGroupItem>
            )
          })}
        </ButtonGroup>
        <Button
          type="button"
          color="tertiary"
          size="sm"
          iconLeading={X}
          aria-label={clearLabel}
          title={clearLabel}
          isDisabled={isDisabled || selected == null}
          onPress={() => field.onChange('')}
          className="shrink-0"
        />
      </div>
      {error?.message && (
        <p className="text-error-600 dark:text-error-400 text-sm">{t(error.message)}</p>
      )}
    </div>
  )
}

export default FormScoreSelect
